import { useState, useEffect } from "react";
import {
  Search,
  Loader2,
  Smartphone,
  Phone,
  CalendarCheck,
  CheckCircle2,
  Mail,
} from "lucide-react";
import apiClient from "@/lib/api-client";
import Button from "@/components/Button";
import { useGlobalToast } from "../../contexts/ToastContext";

interface EndUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string | null;
  phone: string | null;
  countryCode: string;
  authProvider: string;
  emailVerified: boolean;
  pushPlatform: string | null;
  createdAt: string;
  reservationCount: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

const PROVIDER_LABEL: Record<string, string> = {
  email: "Email",
  google: "Google",
  apple: "Apple",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function EndUsersAdmin() {
  const { showToast } = useGlobalToast();
  const [endUsers, setEndUsers] = useState<EndUser[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchEndUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (pagination.page === 1) {
        fetchEndUsers();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchEndUsers = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (searchTerm) params.append("search", searchTerm);

      const response = await apiClient.get(`/admin/endusers?${params}`);
      setEndUsers(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      showToast("Error al cargar los clientes de la app", "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Clientes de la app
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Usuarios finales registrados en la app móvil (entidad global, no ligada
          a una organización)
        </p>
      </div>

      {/* Card principal */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Buscador */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : endUsers.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-2">
            <Smartphone className="h-10 w-10" />
            <p className="text-sm">No hay clientes de la app todavía</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Tabla con scroll propio */}
            <div className="flex-1 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registro
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      App
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Reservas
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Alta
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {endUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      {/* Cliente */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center">
                          <div className="h-8 w-8 flex-shrink-0 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-700 text-xs font-semibold uppercase">
                              {u.firstName?.[0] ?? "?"}
                              {u.lastName?.[0] ?? ""}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">
                              {u.firstName} {u.lastName ?? ""}
                            </div>
                            <div className="text-xs text-gray-500 flex items-center gap-1">
                              <Mail size={11} /> {u.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Teléfono */}
                      <td className="px-4 py-2.5">
                        {u.phone ? (
                          <div className="flex items-center gap-1.5 text-sm text-gray-900">
                            <Phone size={13} className="text-gray-400" />
                            {u.countryCode} {u.phone}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      {/* Registro (proveedor + verificado) */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {PROVIDER_LABEL[u.authProvider] ?? u.authProvider}
                          </span>
                          {u.emailVerified && (
                            <CheckCircle2
                              size={14}
                              className="text-green-500"
                            />
                          )}
                        </div>
                      </td>
                      {/* App (push platform) */}
                      <td className="px-4 py-2.5">
                        {u.pushPlatform ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700">
                            <Smartphone size={12} />
                            {u.pushPlatform === "ios" ? "iOS" : "Android"}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">
                            Sin push
                          </span>
                        )}
                      </td>
                      {/* Reservas */}
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 text-sm text-gray-900">
                          <CalendarCheck size={14} className="text-gray-400" />
                          {u.reservationCount}
                        </div>
                      </td>
                      {/* Alta */}
                      <td className="px-4 py-2.5 text-sm text-gray-500">
                        {formatDate(u.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación fija al fondo */}
            {pagination.totalPages > 1 && (
              <div className="flex-shrink-0 flex items-center justify-between border-t border-gray-200 px-4 py-2.5">
                <div className="text-xs text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  de {pagination.total}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
